import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";

import { AddressFormBlockContextProvider } from "./addressFormBlock.context";
import {
    BusinessAddressFormValues,
    CustomerAddressFormValues
} from "./addressFormBlock.schema";
import { DefaultBusinessAddress } from "./components/defaultBusinessAddress.component";
import { DefaultCustomerAddress } from "./components/defaultCustomerAddress.component";
import { DeliveryBusinessAddress } from "./components/deliveryBusinessAddress.component";
import { DeliveryCustomerAddress } from "./components/deliveryCustomerAddress.component";
import { PaymentBusinessAddress } from "./components/paymentBusinessAddress.component";
import { PaymentCustomerAddress } from "./components/paymentCustomerAddress.component";

type AddressFormBlockProps<TAddressType extends AddressType> = {
    country?: string;
    skipValidation?: boolean;
    addressType: TAddressType;
    addressStatus: AddressValidationStatus;
    disabled?: boolean;
};

const CustomerAddressFormBlockMap = new Map([
    [AddressType.Default, DefaultCustomerAddress],
    [AddressType.Delivery, DeliveryCustomerAddress],
    [AddressType.Payment, PaymentCustomerAddress]
]);

const CustomerAddressFormBlock = <
    _ extends CustomerAddressFormValues<TAddressType>,
    TAddressType extends AddressType
>({
    country,
    skipValidation,
    addressType,
    addressStatus,
    disabled
}: AddressFormBlockProps<TAddressType>) => {
    const methods = useFormContext();
    const { errorFields } = useAddressValidationContext();

    useEffect(() => {
        errorFields?.forEach((errorField) => {
            const key = errorField.key;
            const touched = methods.formState.touchedFields[key];

            if (touched) return;

            methods.setValue(key, errorField.value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false
            });
        });
    }, [errorFields, methods.formState, methods.setValue]);

    const Component = CustomerAddressFormBlockMap.get(addressType);

    if (!Component) {
        return null;
    }

    return (
        <AddressFormBlockContextProvider
            addressType={addressType}
            addressStatus={addressStatus}
            country={country}
            disabled={disabled}
            skipValidation={skipValidation}
        >
            <Component />
        </AddressFormBlockContextProvider>
    );
};

const BusinessAddressFormBlockMap = new Map([
    [AddressType.Default, DefaultBusinessAddress],
    [AddressType.Delivery, DeliveryBusinessAddress],
    [AddressType.Payment, PaymentBusinessAddress]
]);

const BusinessAddressFormBlock = <
    _ extends BusinessAddressFormValues<TAddressType>,
    TAddressType extends AddressType
>({
    country,
    skipValidation,
    addressType,
    addressStatus,
    disabled
}: AddressFormBlockProps<TAddressType>) => {
    const Component = BusinessAddressFormBlockMap.get(addressType);

    if (!Component) {
        return null;
    }

    return (
        <AddressFormBlockContextProvider
            addressType={addressType}
            addressStatus={addressStatus}
            country={country}
            disabled={disabled}
            skipValidation={skipValidation}
        >
            <Component />
        </AddressFormBlockContextProvider>
    );
};

export { BusinessAddressFormBlock, CustomerAddressFormBlock };
